import { useQueryClient } from '@tanstack/react-query'
import last from 'lodash/last'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { MyOptionType } from '@procsea/design-system'

import { TagFormValue } from 'src/components/presentationals/shared/forms'
import { useUser, useUserBuyerCompanyId, useUserSellerCompanyId } from 'src/contexts/userContext'
import { useSegment } from 'src/hooks'
import {
  ArchiveBuyerTourPayload,
  TOURS_QUERY_KEY as BUYER_TOURS_QUERY_KEY,
  PostBuyerTourPayload,
  useArchiveBuyerTourMutation,
  usePaginatedBuyerToursQuery,
  usePostBuyerTourMutation,
} from 'src/queries/buyer/tours'
import {
  ArchiveSellerTourPayload,
  PostSellerTourPayload,
  TOURS_QUERY_KEY as SELLER_TOURS_QUERY_KEY,
  useArchiveSellerTourMutation,
  usePaginatedSellerToursQuery,
  usePostSellerTourMutation,
} from 'src/queries/seller/tours/tours'
import { listAllPaginatedEntities } from 'src/queries/utils'
import { BuyerTour, SellerTour, Tour } from 'src/types'

import { dispatchCreateNotification } from '../../../../../action-creators'

export const getTourTagsSelectOptions = (tours: Tour[] = []): MyOptionType<Id>[] =>
  tours.map(({ name, id }) => ({ label: name, value: id }))

interface UseToursArgs {
  hasMultiMemberships?: boolean
}

export interface UseToursReturn {
  isLoading: boolean
  onSubmit: (form: TagFormValue) => Promise<Id>
  onArchive: (id: Id) => Promise<void>
  tourOptions: MyOptionType<Id>[]
}

export const useTours = ({ hasMultiMemberships }: UseToursArgs = {}): UseToursReturn => {
  const user = useUser()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { track } = useSegment()

  const sellerCompanyIds = user?.memberships
    .filter(membership => membership.isSeller)
    .map(membership => membership.sellerCompanyId) as Id[]

  const buyerCompanyId = useUserBuyerCompanyId()
  const sellerCompanyId = useUserSellerCompanyId()

  const buyerQuery = usePaginatedBuyerToursQuery({
    queryParams: { archived: false },
  })

  const sellerQuery = usePaginatedSellerToursQuery({
    queryParams: {
      sellerCompanyId: hasMultiMemberships ? sellerCompanyIds : sellerCompanyId,
      archived: false,
    },
  })

  const lastBuyerNextPage = last(buyerQuery.data?.pages)?.next
  const lastSellerNextPage = last(sellerQuery.data?.pages)?.next

  useEffect(() => {
    if (lastBuyerNextPage) {
      buyerQuery.fetchNextPage()
    }
  }, [lastBuyerNextPage, buyerQuery.fetchNextPage])

  useEffect(() => {
    if (lastSellerNextPage) {
      sellerQuery.fetchNextPage()
    }
  }, [lastSellerNextPage, sellerQuery.fetchNextPage])

  const isLoading = buyerQuery.isInitialLoading || sellerQuery.isInitialLoading

  const tours = useMemo(
    () => listAllPaginatedEntities<BuyerTour | SellerTour>(buyerQuery.data || sellerQuery.data),
    [JSON.stringify([buyerQuery.data, sellerQuery.data])]
  )
  const tourOptions = useMemo(() => getTourTagsSelectOptions(tours), [JSON.stringify(tours)])

  const { mutateAsync: buyerMutateAsync } = usePostBuyerTourMutation()
  const { mutateAsync: sellerMutateAsync } = usePostSellerTourMutation()

  const onCreateError = useCallback((error: Error) => {
    dispatchCreateNotification(dispatch)({
      message: gettext('An error occurred while creating the tour'),
      type: 'negative',
    })

    return Promise.reject(error)
  }, [])

  const onCreateSuccess = useCallback((response: BuyerTour | SellerTour): Id => {
    dispatchCreateNotification(dispatch)({
      message: gettext('Your tour has been successfully created'),
      type: 'positive',
    })

    return response.id
  }, [])

  const postBuyerTour = useCallback(
    (payload: PostBuyerTourPayload): Promise<Id> =>
      buyerMutateAsync(payload)
        .then((response: BuyerTour) => {
          queryClient.invalidateQueries({ queryKey: [BUYER_TOURS_QUERY_KEY] })

          return onCreateSuccess(response)
        })
        .catch(onCreateError),
    [buyerMutateAsync, onCreateSuccess, onCreateError]
  )

  const postSellerTour = useCallback(
    (payload: PostSellerTourPayload): Promise<Id> =>
      sellerMutateAsync(payload)
        .then((response: SellerTour) => {
          queryClient.invalidateQueries({ queryKey: [SELLER_TOURS_QUERY_KEY] })

          return onCreateSuccess(response)
        })
        .catch(onCreateError),
    [sellerMutateAsync, onCreateSuccess, onCreateError]
  )

  const onSubmit: (form: TagFormValue) => Promise<Id> = useCallback(
    (form: TagFormValue) => {
      if (buyerCompanyId) {
        return postBuyerTour({ ...form, buyerCompanyId })
      }

      if (sellerCompanyId) {
        return postSellerTour({ ...form, sellerCompanyId })
      }

      return Promise.reject(new Error('Tour owner must be identified'))
    },
    [buyerCompanyId, postBuyerTour, postSellerTour, sellerCompanyId]
  )

  const { mutateAsync: buyerArchiveAsync } = useArchiveBuyerTourMutation()
  const { mutateAsync: sellerArchiveAsync } = useArchiveSellerTourMutation()

  const onArchiveError = useCallback((error: Error) => {
    dispatchCreateNotification(dispatch)({
      message: gettext('An error occurred while archiving the tour'),
      type: 'negative',
    })

    return Promise.reject(error)
  }, [])

  const onArchiveSuccess = useCallback(() => {
    dispatchCreateNotification(dispatch)({
      message: gettext('Your tour has been successfully archived'),
      type: 'positive',
    })

    return Promise.resolve()
  }, [])

  const archiveBuyerTour = useCallback(
    (payload: ArchiveBuyerTourPayload): Promise<void> =>
      buyerArchiveAsync(payload)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: [BUYER_TOURS_QUERY_KEY] })
          track('Buyer - Tour Archived', { tourId: payload.id })

          return onArchiveSuccess()
        })
        .catch(onArchiveError),
    [buyerArchiveAsync, onArchiveSuccess, onArchiveError]
  )

  const archiveSellerTour = useCallback(
    (payload: ArchiveSellerTourPayload): Promise<void> =>
      sellerArchiveAsync(payload)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: [SELLER_TOURS_QUERY_KEY] })

          return onArchiveSuccess()
        })
        .catch(onArchiveError),
    [sellerArchiveAsync, onArchiveSuccess, onArchiveError]
  )

  const onArchive = useCallback(
    (id: Id) => {
      if (buyerCompanyId) {
        return archiveBuyerTour({ id, archived: true })
      }

      if (sellerCompanyId) {
        return archiveSellerTour({ id, archived: true })
      }

      return Promise.reject(new Error('Tour owner must be identified'))
    },
    [buyerCompanyId, archiveBuyerTour, archiveSellerTour, sellerCompanyId]
  )

  return { tourOptions, isLoading, onSubmit, onArchive }
}
